import { match } from 'ts-pattern'
import i18next from 'i18next'
import type { VolumedAssignMutator } from '@/lib/entity/volumed_assign'
import type { Converter } from '@/lib/converter/entityFormatter'
import { layered, withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'
import { sql } from 'kysely'
import { asModifiedField } from '@/lib/converter/entityFormatter'

type VolumedAssignSchema = VolumedAssignMutator['merged']

export const VolumedAssignConverter: Converter<VolumedAssignSchema> = {
  icon: () => i18next.t('stages.icon.volumed_assign'),
  title: () => i18next.t('stages.table.volumed_assign'),
  subTitle: async (merged) => {
    const queryResult = await assignQuery(merged.id!)
    const subtitleValue = queryResult.nameConcat
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!subtitleValue) return ''

    if ('merged' in subtitleValue && typeof subtitleValue.merged === 'function') {
      return subtitleValue.merged() ?? subtitleValue.get('BASE') ?? ''
    }
    return String(subtitleValue)
  },
  modifiedFields: (modified, column) => {
    const label = i18next.t(`stages.labels.volumed_assign.${column}`)
    return match(column)
      .with('comment', 'visible', (column) => asModifiedField(label, modified[column]!))
      .with('icon', () => {
        if (modified.icon === null || modified.icon === undefined) {
          return asModifiedField(label, i18next.t('ui.projectHistory.noData'))
        }
        const emoji = String.fromCodePoint(parseInt(modified.icon, 16))
        return asModifiedField(label, emoji)
      })
      .with('job_title_id', async () =>
        asModifiedField(label, await getJobTitleName(modified.job_title_id!))
      )
      .with('jobSkillIds', async () => {
        const jobSkillIds = removeUndefinedFromArray(modified.jobSkillIds!)
        const jobSkillNames = await getJobSkillNames(jobSkillIds)
        return asModifiedField(label, jobSkillNames)
      })
      .with('departmentIds', async () => {
        const departmentIds = removeUndefinedFromArray(modified.departmentIds!)
        const departmentNames = await getDepartmentNames(departmentIds)
        return asModifiedField(label, departmentNames)
      })
      .with('id', 'project_id', () => undefined)
      .exhaustive()
  }
}
const assignQuery = async (id: string) => {
  return await layered(async (db: Kysely<DB>) => {
    const result = await db
      .selectFrom('volumed_assign')
      .innerJoin('project', 'volumed_assign.project_id', 'project.id')
      .innerJoin('job_title', 'volumed_assign.job_title_id', 'job_title.id')
      .select([sql<string>`project.name || ' x ' || job_title.name`.as('nameConcat')])
      .where('volumed_assign.id', '=', id)
      .executeTakeFirst()
    if (!result) {
      return { nameConcat: null }
    }
    return result
  })
}

const getJobTitleName = async (id: string) => {
  const result = await withStage(async (db: Kysely<DB>) => {
    return (
      (await db
        .selectFrom('job_title')
        .select(['name'])
        .where('id', '=', id)
        .executeTakeFirst()) || { name: '' }
    )
  })
  return result.name
}

const getJobSkillNames = async (ids: string[]): Promise<string> => {
  const result = await withStage(async (db: Kysely<DB>) => {
    return (
      (await db
        .selectFrom('job_skill')
        .select([sql<string>`IFNULL(group_concat(name),'')`.as('names')])
        .where('id', 'in', ids)
        .executeTakeFirst()) || { names: '' }
    )
  })
  return truncateString(result.names)
}

const getDepartmentNames = async (ids: string[]): Promise<string> => {
  const result = await withStage(async (db: Kysely<DB>) => {
    return (
      (await db
        .selectFrom('department')
        .select([sql<string>`IFNULL(group_concat(name),'')`.as('names')])
        .where('id', 'in', ids)
        .executeTakeFirst()) || { names: '' }
    )
  })
  return truncateString(result.names)
}

const truncateString = (str: string, length = 15): string =>
  str.length > length ? str.substring(0, length) + '...' : str

const removeUndefinedFromArray = (ids: (string | undefined)[]): string[] =>
  ids.filter((item): item is string => item !== undefined)
